import { CompletionCreateParams } from "openai/resources/chat";
import React, { ReactNode, useCallback, useState } from "react";
import { CopilotContext } from "../context/copilot-context";
import useTree from "../hooks/use-tree";
import { AnnotatedFunction } from "../types/annotated-function";
import { FunctionCallHandler } from "../types/ai-function";

export function CopilotProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [entryPoints, setEntryPoints] = useState<
    Record<string, AnnotatedFunction<any[]>>
  >({});

  const { addElement, removeElement, printTree } = useTree();

  const setEntryPoint = useCallback(
    (id: string, entryPoint: AnnotatedFunction<any[]>) => {
      setEntryPoints((prevPoints) => {
        return {
          ...prevPoints,
          [id]: entryPoint,
        };
      });
    },
    [],
  );

  const removeEntryPoint = useCallback((id: string) => {
    setEntryPoints((prevPoints) => {
      const newPoints = { ...prevPoints };
      delete newPoints[id];
      return newPoints;
    });
  }, []);

  const getContextString = useCallback(
    (categories: string[] = ["global"]) => {
      return printTree(categories);
    },
    [printTree],
  );

  const addContext = useCallback(
    (context: string, parentId?: string, categories: string[] = ["global"]) => {
      return addElement(context, categories, parentId);
    },
    [addElement],
  );

  const removeContext = useCallback(
    (id: string) => {
      removeElement(id);
    },
    [removeElement],
  );

  const getChatCompletionFunctionDescriptions = useCallback(() => {
    return entryPointsToChatCompletionFunctions(Object.values(entryPoints));
  }, [entryPoints]);

  const getFunctionCallHandler = useCallback(() => {
    return entryPointsToFunctionCallHandler(Object.values(entryPoints));
  }, [entryPoints]);

  return (
    <CopilotContext.Provider
      value={{
        entryPoints,
        getChatCompletionFunctionDescriptions,
        getFunctionCallHandler,
        setEntryPoint,
        removeEntryPoint,
        getContextString,
        addContext,
        removeContext,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
}

function entryPointsToFunctionCallHandler(
  entryPoints: AnnotatedFunction<any[]>[],
): FunctionCallHandler {
  return async (chatMessages, functionCall) => {
    let entrypointsByFunctionName: Record<
      string,
      AnnotatedFunction<any[]>
    > = {};
    for (let entryPoint of entryPoints) {
      entrypointsByFunctionName[entryPoint.name] = entryPoint;
    }

    const entryPointFunction =
      entrypointsByFunctionName[functionCall.name || ""];
    if (entryPointFunction) {
      let parsedFunctionCallArguments: Record<string, any>[] = [];
      if (functionCall.arguments) {
        parsedFunctionCallArguments = JSON.parse(functionCall.arguments);
      }

      const paramsInCorrectOrder: any[] = [];
      for (let arg of entryPointFunction.argumentAnnotations) {
        paramsInCorrectOrder.push(
          parsedFunctionCallArguments[
            arg.name as keyof typeof parsedFunctionCallArguments
          ],
        );
      }

      await entryPointFunction.implementation(...paramsInCorrectOrder);
    }
  };
}

function entryPointsToChatCompletionFunctions(
  entryPoints: AnnotatedFunction<any[]>[],
): CompletionCreateParams.Function[] {
  return entryPoints.map(annotatedFunctionToChatCompletionFunction);
}

function annotatedFunctionToChatCompletionFunction(
  annotatedFunction: AnnotatedFunction<any[]>,
): CompletionCreateParams.Function {
  let parameters: { [key: string]: any } = {};
  for (let arg of annotatedFunction.argumentAnnotations) {
    let { name, required, ...forwardedArgs } = arg;
    parameters[arg.name] = forwardedArgs;
  }

  let requiredParameterNames: string[] = [];
  for (let arg of annotatedFunction.argumentAnnotations) {
    if (arg.required) {
      requiredParameterNames.push(arg.name);
    }
  }

  let chatCompletionFunction: CompletionCreateParams.Function = {
    name: annotatedFunction.name,
    description: annotatedFunction.description,
    parameters: {
      type: "object",
      properties: parameters,
      required: requiredParameterNames,
    },
  };
  return chatCompletionFunction;
}
